/**
 * Deletion — lifecycle Phase C (17 Jul). Only reachable from the ARCHIVED
 * state (two-step by design, like Timetastic — but unlike them we offer
 * ANONYMISE as the default: the person becomes "Former employee", their
 * email is freed, but their bookings survive so historical reports stay
 * true. Full erase is there for hard GDPR requests.
 *
 * The download link exports everything held on them BEFORE the kill, and the
 * confirm requires typing their full name (re-checked server-side).
 */
import React, { useState } from 'react';
import { AlertTriangle, Download, Loader2, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { usersApi } from '../lib/api';
import { toast } from 'sonner';

export default function DeleteEmployeeDialog({ open, onOpenChange, employee, onSuccess }) {
  const [mode, setMode] = useState('anonymise');
  const [confirmName, setConfirmName] = useState('');
  const [busy, setBusy] = useState(false);

  const fullName = employee ? `${employee.first_name} ${employee.last_name}`.trim() : '';
  const nameMatches = confirmName.trim().toLowerCase() === fullName.toLowerCase();

  const download = async () => {
    try {
      // The server sends a real Excel workbook (Rob, 17 Jul: JSON means
      // nothing to most admins).
      const r = await usersApi.exportUser(employee.id);
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fullName.replace(/\s+/g, '-')}-data.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Could not export their data');
    }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const r = await usersApi.deletePermanently(employee.id, {
        mode, confirm_name: confirmName,
      });
      toast.success(mode === 'erase'
        ? `Erased. ${r.data.email_freed} is free to use again.`
        : `Anonymised — their history remains as "Former employee". ${r.data.email_freed} is free to use again.`);
      onOpenChange(false);
      setConfirmName('');
      if (onSuccess) onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  if (!employee) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) setConfirmName(''); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-red-500">
            Delete {fullName} permanently
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
            <p>
              This cannot be undone. Their email address becomes free to use
              again, and billing for this seat stops.
            </p>
          </div>

          <Button type="button" variant="outline" onClick={download} className="w-full" data-testid="delete-export">
            <Download className="w-4 h-4 mr-2" />
            Download everything we hold on them first
          </Button>

          <div className="space-y-2">
            <label className="flex items-start gap-3 cursor-pointer p-2 rounded-lg hover:bg-secondary/40">
              <input type="radio" checked={mode === 'anonymise'}
                     onChange={() => setMode('anonymise')} className="mt-1 accent-primary" />
              <span>
                <span className="block text-sm font-medium">Anonymise (recommended)</span>
                <span className="block text-xs text-muted-foreground">
                  They become "Former employee" — personal details gone, but their
                  bookings stay, so last year's reports and wallcharts remain true.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer p-2 rounded-lg hover:bg-secondary/40">
              <input type="radio" checked={mode === 'erase'}
                     onChange={() => setMode('erase')} className="mt-1 accent-primary" />
              <span>
                <span className="block text-sm font-medium">Erase everything</span>
                <span className="block text-xs text-muted-foreground">
                  Full removal, bookings included — past reports will lose their
                  rows. For formal "right to erasure" requests.
                </span>
              </span>
            </label>
          </div>

          <div>
            <Label>Type their full name to confirm: <span className="font-semibold">{fullName}</span></Label>
            <Input value={confirmName} onChange={(e) => setConfirmName(e.target.value)}
                   className="input-hive mt-1" placeholder={fullName}
                   data-testid="delete-confirm-name" />
          </div>

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
              Cancel
            </Button>
            <Button type="button"
                    className="bg-red-600 hover:bg-red-700 text-white"
                    onClick={confirm}
                    disabled={busy || !nameMatches}
                    data-testid="delete-confirm">
              {busy ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Deleting…</>)
                : (<><Trash2 className="w-4 h-4 mr-2" />{mode === 'erase' ? 'Erase everything' : 'Anonymise'}</>)}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
